// @figma file=pkCGby54zXzTsccXvHqzO6 node=3884:9073 name="Default Select" set="mytaible-shadcn-studio" verified=2026-03-21

import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Trigger Variants ─────────────────────────────────────────────────────────

/**
 * CVA variants for SelectTrigger — maps Figma Size + Shape properties.
 *
 * Figma sizes: xs → sm, sm → sm, md → default, lg → lg
 * Figma shapes: Rounded → rounded-[var(--field-radius,var(--radius))], Round → rounded-full
 */
const selectTriggerVariants = cva(
  [
    // ── Layout ────────────────────────────────────────────────────────────────
    'flex w-full items-center justify-between gap-2 whitespace-nowrap',
    // ── Colours & border — Figma: fill_9HZ58U (background) + stroke_1JDIN3 (border) ──
    'border border-input bg-input-bg text-foreground shadow-sm',
    // ── Placeholder text ─────────────────────────────────────────────────────
    '[&_[data-placeholder]]:text-muted-foreground',
    // ── Focus ring ────────────────────────────────────────────────────────────
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
    // ── Disabled — Figma: State=Disabled ─────────────────────────────────────
    'disabled:cursor-not-allowed disabled:opacity-50',
    // ── Cursor ───────────────────────────────────────────────────────────────
    'cursor-pointer',
    // ── Truncate overflow ────────────────────────────────────────────────────
    '[&>span]:truncate [&>span]:flex-1 [&>span]:text-left',
  ],
  {
    variants: {
      size: {
        /** Figma: Size=xs / Size=sm → compact trigger */
        sm: 'h-8 px-3 text-base sm:text-xs',
        /** Figma: Size=md → standard trigger (default) */
        default: 'h-9 px-3 py-2 text-base sm:text-sm',
        /** Figma: Size=lg → large trigger */
        lg: 'h-10 px-4 text-base',
      },
      shape: {
        /** Figma: Shape=Rounded — 8px corner radius */
        rounded: 'rounded-[var(--field-radius,var(--radius))]',
        /** Figma: Shape=Round — pill shape */
        round: 'rounded-full',
      },
    },
    defaultVariants: { size: 'default', shape: 'rounded' },
  },
);

// ─── Props ────────────────────────────────────────────────────────────────────

/** Props for the root Select — maps to Radix Select.Root API */
export interface SelectProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> {}

/** Props for the Select trigger button */
export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {}

/** Props for the dropdown content panel */
export interface SelectContentProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {}

/** Props for an individual selectable item */
export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {}

/** Props for a group of items */
export interface SelectGroupProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group> {}

/** Props for a group label */
export interface SelectLabelProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> {}

/** Props for a visual separator between item groups */
export interface SelectSeparatorProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> {}

/** Props for SelectValue — renders the selected option's text */
export interface SelectValueProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value> {}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Root controller for the Select.  Accepts `value`, `onValueChange`,
 * `defaultValue`, `disabled`, `required`, `open`, and `onOpenChange`.
 *
 * @example
 * <Select onValueChange={setValue}>
 *   <SelectTrigger><SelectValue placeholder="Pick a fruit" /></SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="apple">Apple</SelectItem>
 *   </SelectContent>
 * </Select>
 */
const Select = SelectPrimitive.Root;
Select.displayName = 'Select';

/**
 * Groups related items inside a SelectContent.
 * Pair with `<SelectLabel>` to announce the group to assistive technology.
 */
const SelectGroup = SelectPrimitive.Group;
SelectGroup.displayName = 'SelectGroup';

/**
 * Renders the currently-selected value inside the trigger.
 * Pass `placeholder` to display text when no value is chosen.
 */
const SelectValue = SelectPrimitive.Value;
SelectValue.displayName = 'SelectValue';

/**
 * Scroll up button shown inside the content when there are items above the
 * visible area.  Rendered automatically by SelectContent.
 */
const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1 text-muted-foreground',
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" aria-hidden="true" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = 'SelectScrollUpButton';

/**
 * Scroll down button shown inside the content when there are items below the
 * visible area.  Rendered automatically by SelectContent.
 */
const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1 text-muted-foreground',
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" aria-hidden="true" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = 'SelectScrollDownButton';

/**
 * The trigger button that opens the dropdown.
 *
 * Size variants map to Figma: `sm` (xs/sm), `default` (md), `lg` (lg).
 * Shape variants: `rounded` (Figma: Shape=Rounded) and `round` (Figma: Shape=Round).
 *
 * @example
 * <SelectTrigger size="lg" shape="round">
 *   <SelectValue placeholder="Select…" />
 * </SelectTrigger>
 */
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, size, shape, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ size, shape }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = 'SelectTrigger';

/**
 * The floating dropdown panel.
 *
 * Defaults to `position="popper"` (anchored below the trigger).
 * Includes scroll up/down buttons for long lists.
 *
 * @example
 * <SelectContent>
 *   <SelectItem value="a">Option A</SelectItem>
 * </SelectContent>
 */
const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        // ── Base ────────────────────────────────────────────────────────────
        'relative z-50 max-h-[min(var(--radix-select-content-available-height),24rem)] min-w-[8rem] overflow-hidden',
        'rounded-[min(var(--radius),0.75rem)] border border-border bg-popover text-popover-foreground shadow-md',
        // ── Open / close animations ─────────────────────────────────────────
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        // ── Slide-in direction (popper) ──────────────────────────────────────
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        // ── Popper sizing ────────────────────────────────────────────────────
        position === 'popper' && [
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1',
          'data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        ],
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = 'SelectContent';

/**
 * Label for a group of items — announces the group name to screen readers.
 *
 * @example
 * <SelectGroup>
 *   <SelectLabel>Fruits</SelectLabel>
 *   <SelectItem value="apple">Apple</SelectItem>
 * </SelectGroup>
 */
const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  SelectLabelProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-xs font-medium text-muted-foreground', className)}
    {...props}
  />
));
SelectLabel.displayName = 'SelectLabel';

/**
 * A single selectable option inside the dropdown.
 *
 * Renders a `Check` icon when the item is the currently-selected value.
 * Set `disabled` to prevent selection.
 *
 * @example
 * <SelectItem value="apple">Apple</SelectItem>
 * <SelectItem value="mango" disabled>Mango (unavailable)</SelectItem>
 */
const SelectItem = React.forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, SelectItemProps>(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        // ── Layout ──────────────────────────────────────────────────────────
        'relative flex w-full cursor-pointer select-none items-center gap-2',
        'rounded-[calc(var(--radius)-2px)] py-1.5 pl-2 pr-8 text-sm',
        // ── Focus / hover ─────────────────────────────────────────────────
        'outline-none focus:bg-accent focus:text-accent-foreground',
        // ── Disabled ──────────────────────────────────────────────────────
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      {/* Check indicator — shown when item is selected */}
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" aria-hidden="true" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = 'SelectItem';

/**
 * A visual separator between groups of items.
 *
 * @example
 * <SelectItem value="a">A</SelectItem>
 * <SelectSeparator />
 * <SelectItem value="b">B</SelectItem>
 */
const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  SelectSeparatorProps
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border', className)}
    {...props}
  />
));
SelectSeparator.displayName = 'SelectSeparator';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  selectTriggerVariants,
};
